import { newPiece } from '../game/piece';
import { parseBoard } from '../board/board';
import type { Board } from '../board/types';
import type { Roster } from '../characters/types';
import { attackerWinProbability } from '../game/battle';
import { CLASSIC_LUMINOSITY_START } from '../game/luminosity';
import type { GameContext, GameState } from '../game/types';

/** The attacker's odds in one duel under the three luminosity situations that matter to it. */
export interface DuelOdds {
  /** Neither side has a luminosity bonus. */
  neutral: number;
  /** The attacker fights at its own luminosity peak, the defender at its worst. */
  best: number;
  /** The attacker at its worst luminosity, the defender at its peak. */
  worst: number;
}

/** `matrix[attacker][defender]` for every ordered pair of roster characters, the mirror included. */
export type DuelMatrix = Readonly<Record<string, Readonly<Record<string, DuelOdds>>>>;

/**
 * The fights are scored by the real `attackerWinProbability` on a private tiny board (the
 * smallest the validator allows): a light attacker against a dark defender on a permanently
 * light square gives the attacker the full bonus and the defender none (`best`), a dark square
 * the other way round (`worst`), and the lab's switch (`GameContext.luminosity`) gives neither
 * side a bonus (`neutral`). A neutral side is no way to say that: it has a bonus of its own
 * (D-32). The board never appears in a game; it only fixes the luminosity so the roster numbers
 * alone decide the odds.
 */
const DUEL_LANE: Board = parseBoard({
  id: 'duel-lane',
  name: 'Duel lane',
  width: 2,
  height: 2,
  cells: ['LD', 'LD'],
  powerPoints: [],
  factions: [
    { id: 'light', name: 'Light', color: '#ffffff', affinity: 'light' },
    { id: 'dark', name: 'Dark', color: '#000000', affinity: 'dark' },
    { id: 'plain-attacker', name: 'Plain attacker', color: '#888888', affinity: 'neutral' },
    { id: 'plain-defender', name: 'Plain defender', color: '#777777', affinity: 'neutral' },
  ],
  // A board must place every side; these stand-ins never become pieces (see `odds`).
  placements: [
    { faction: 'light', character: 'duelist', x: 0, y: 0 },
    { faction: 'dark', character: 'duelist', x: 1, y: 0 },
    { faction: 'plain-attacker', character: 'duelist', x: 0, y: 1 },
    { faction: 'plain-defender', character: 'duelist', x: 1, y: 1 },
  ],
});

function odds(
  roster: Roster,
  attacker: string,
  defender: string,
  factions: [string, string],
  square: number,
  luminosity?: 'off',
): number {
  const ctx: GameContext = { board: DUEL_LANE, roster, ...(luminosity ? { luminosity } : {}) };
  const state: GameState = {
    turn: 0,
    round: 0,
    lastProgressTurn: 0,
    factions: [],
    activeFaction: factions[0],
    pieces: [
      newPiece(ctx.roster, 1, factions[0], attacker, 1 - square, 0),
      newPiece(ctx.roster, 2, factions[1], defender, square, 0),
    ],
    nextPieceId: 3,
    luminosity: CLASSIC_LUMINOSITY_START,
    phase: 'battle',
    pendingBattle: { attackerId: 1, defenderId: 2, x: square, y: 0 },
    winner: null,
    rng: 0,
  };
  return attackerWinProbability(ctx, state);
}

/** The attacker's odds against one defender, both unwounded, with no spells cast. */
export function duelOdds(roster: Roster, attacker: string, defender: string): DuelOdds {
  return {
    neutral: odds(roster, attacker, defender, ['plain-attacker', 'plain-defender'], 0, 'off'),
    best: odds(roster, attacker, defender, ['light', 'dark'], 0),
    worst: odds(roster, attacker, defender, ['light', 'dark'], 1),
  };
}

/** Every ordered pair of the roster; `matrix[a][d]` is a's odds when it attacks d. */
export function duelMatrix(ctx: Pick<GameContext, 'roster'>): DuelMatrix {
  const ids = Object.keys(ctx.roster);
  const matrix: Record<string, Record<string, DuelOdds>> = {};
  for (const attacker of ids) {
    const row: Record<string, DuelOdds> = {};
    for (const defender of ids) row[defender] = duelOdds(ctx.roster, attacker, defender);
    matrix[attacker] = row;
  }
  return matrix;
}
